import type { FilterState } from '@/components/filter-bar/filter-bar';
import type { DocumentsPreference, DocumentSearchParams } from '@vagusx/clients';
import { toUtcDateRange } from '@/lib/date-range';
import { URGENCY_VALUES } from './constants';

// ---------------------------------------------------------------------------
// URL ↔ FilterState
//
// URL is the in-session source of truth — both the FilterBar UI state and
// the API request params derive from it. The fresh-user default
// (`classification=referral`) is intentionally NOT injected here; it lives
// in `usePersistentDocFilters` so a user who explicitly clears all filters
// stays cleared instead of oscillating back to the default.
// ---------------------------------------------------------------------------

const VALID_URGENCY = new Set<string>(URGENCY_VALUES);

// Local-day strings only — `2026-04-24`. Anything else is dropped before
// reaching `toUtcDateRange`, which would otherwise cheerfully forward
// nonsense.
const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

function isValidDateString(s: string | undefined): s is string {
  return typeof s === 'string' && DATE_SHAPE.test(s);
}

/**
 * Convert URL search params into FilterState for the FilterBar. Unknown
 * multi-select values and malformed dates are silently dropped.
 */
export function urlToFilterState(params: Record<string, string | undefined>): FilterState {
  const state: FilterState = {};

  if (params.urgency) {
    const values = params.urgency.split(',').filter((v) => VALID_URGENCY.has(v));
    if (values.length > 0) state.urgency = { type: 'multi-select', values };
  }

  if (params.classification) {
    // No allowlist — document-type values are tenant-defined classification
    // labels (category=document_type), not a static enum. Backend matches
    // exactly via `inArray` against `documents.classification` so unknown
    // values are harmless: they just produce zero rows. Trim before
    // filtering so whitespace-only entries (e.g. `?classification=a, ,b`)
    // don't render as blank chips.
    const values = params.classification
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (values.length > 0) state.classification = { type: 'multi-select', values };
  }

  if (params.referringProviderName) {
    state.referringProviderName = {
      type: 'input',
      operator: 'contains',
      value: params.referringProviderName,
    };
  }

  if (params.healthPlan) {
    state.healthPlan = {
      type: 'input',
      operator: 'contains',
      value: params.healthPlan,
    };
  }

  const fromOk = isValidDateString(params.dateFrom) ? params.dateFrom : '';
  const toOk = isValidDateString(params.dateTo) ? params.dateTo : '';
  if (fromOk || toOk) {
    state.date = { type: 'date-range', from: fromOk, to: toOk };
  }

  return state;
}

/**
 * Convert FilterState back into URL search params. Empty filters omitted.
 */
export function filterStateToUrl(state: FilterState): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};

  const urgency = state.urgency;
  if (urgency?.type === 'multi-select' && urgency.values.length > 0) {
    params.urgency = urgency.values.join(',');
  }

  const classification = state.classification;
  if (classification?.type === 'multi-select' && classification.values.length > 0) {
    params.classification = classification.values.join(',');
  }

  const referringProvider = state.referringProviderName;
  if (referringProvider?.type === 'input' && referringProvider.value) {
    params.referringProviderName = referringProvider.value;
  }

  const healthPlan = state.healthPlan;
  if (healthPlan?.type === 'input' && healthPlan.value) {
    params.healthPlan = healthPlan.value;
  }

  const date = state.date;
  if (date?.type === 'date-range') {
    if (date.from) params.dateFrom = date.from;
    if (date.to) params.dateTo = date.to;
  }

  return params;
}

// ---------------------------------------------------------------------------
// FilterState → API request params
// ---------------------------------------------------------------------------

/**
 * Convert FilterState into API search params. Date range is converted from
 * local-day strings to UTC instants so the server can compare directly
 * against TIMESTAMPTZ columns.
 */
export function filterStateToApiParams(
  state: FilterState
): Omit<DocumentSearchParams, 'limit' | 'offset' | 'searchTerm'> {
  const result: Omit<DocumentSearchParams, 'limit' | 'offset' | 'searchTerm'> = {};

  const urgency = state.urgency;
  if (urgency?.type === 'multi-select' && urgency.values.length > 0) {
    result.urgency = urgency.values.join(',');
  }

  const classification = state.classification;
  if (classification?.type === 'multi-select' && classification.values.length > 0) {
    result.classification = classification.values.join(',');
  }

  const referringProvider = state.referringProviderName;
  if (referringProvider?.type === 'input' && referringProvider.value) {
    result.referringProviderName = referringProvider.value;
  }

  const healthPlan = state.healthPlan;
  if (healthPlan?.type === 'input' && healthPlan.value) {
    result.healthPlan = healthPlan.value;
  }

  const date = state.date;
  if (date?.type === 'date-range') {
    const { dateFrom, dateTo } = toUtcDateRange({ from: date.from, to: date.to });
    if (dateFrom) result.dateFrom = dateFrom;
    if (dateTo) result.dateTo = dateTo;
  }

  return result;
}

// ---------------------------------------------------------------------------
// URL ↔ DocumentsPreference (cross-session persistence)
//
// Mirrors the URL params one-to-one — round-trips through navigate() with
// no shape conversion. Search term, pagination, and sort are NOT persisted.
// ---------------------------------------------------------------------------

export function urlToDocumentsPreference(
  params: Record<string, string | undefined>
): DocumentsPreference {
  const pref: DocumentsPreference = {};
  if (params.urgency) pref.urgency = params.urgency;
  if (params.classification) pref.classification = params.classification;
  if (params.referringProviderName) pref.referringProviderName = params.referringProviderName;
  if (params.healthPlan) pref.healthPlan = params.healthPlan;
  if (isValidDateString(params.dateFrom)) pref.dateFrom = params.dateFrom;
  if (isValidDateString(params.dateTo)) pref.dateTo = params.dateTo;
  return pref;
}

export function documentsPreferenceToUrl(pref: DocumentsPreference): Record<string, string> {
  const params: Record<string, string> = {};
  if (pref.urgency) params.urgency = pref.urgency;
  if (pref.classification) params.classification = pref.classification;
  if (pref.referringProviderName) params.referringProviderName = pref.referringProviderName;
  if (pref.healthPlan) params.healthPlan = pref.healthPlan;
  if (isValidDateString(pref.dateFrom)) params.dateFrom = pref.dateFrom;
  if (isValidDateString(pref.dateTo)) params.dateTo = pref.dateTo;
  return params;
}

export function hasAnyFilterParam(params: Record<string, string | undefined>): boolean {
  return !!(
    params.urgency ||
    params.classification ||
    params.referringProviderName ||
    params.healthPlan ||
    params.dateFrom ||
    params.dateTo
  );
}
